import type { InputConfig } from "./config";
import type {
  InputSample,
  RollProgressAction,
  RollLapAction,
  InputListener,
} from "./types";
import { MAX_ROLL_RATE } from "./types";

interface TouchState {
  samples: Array<{ x: number; y: number; t: number }>;
  totalRadians: number;
  completedLaps: number;
  centroid: { x: number; y: number } | null;
}

export class RollDetector {
  private active = false;
  private touches = new Map<number, TouchState>();
  private listener: InputListener | null = null;
  private lastLapTimes: number[] = [];
  private nowTick: () => number;
  private config: InputConfig;

  constructor(config: InputConfig, nowTick: () => number) {
    this.config = config;
    this.nowTick = nowTick;
  }

  setActive(active: boolean): void {
    this.active = active;
    if (!active) {
      this.touches.clear();
    }
  }

  onInput(listener: InputListener): void {
    this.listener = listener;
  }

  processSample(sample: InputSample): void {
    if (!this.active) return;

    const { pointerId, phase, x, y, t } = sample;

    if (phase === "start") {
      this.touches.set(pointerId, {
        samples: [{ x, y, t }],
        totalRadians: 0,
        completedLaps: 0,
        centroid: null,
      });
    } else if (phase === "move") {
      const state = this.touches.get(pointerId);
      if (!state) return;

      state.samples.push({ x, y, t });

      // Keep only recent samples (last 500ms)
      const cutoff = t - 500;
      state.samples = state.samples.filter((s) => s.t >= cutoff);

      // Use a stable centroid: calculate it once after we have enough samples
      if (!state.centroid && state.samples.length >= 5) {
        state.centroid = this.calculateCentroid(state.samples);
      }

      if (!state.centroid) return;

      const centroid = state.centroid;

      // Calculate current radius
      const radius = Math.sqrt(
        Math.pow(x - centroid.x, 2) + Math.pow(y - centroid.y, 2),
      );

      const maxRadius = this.config.screenWidth * this.config.rollMaxRadiusPct;

      // Calculate angle increment before radius check so we can accumulate angles
      if (state.samples.length >= 2) {
        const prev = state.samples[state.samples.length - 2];
        if (!prev) return;
        const angle1 = Math.atan2(prev.y - centroid.y, prev.x - centroid.x);
        const angle2 = Math.atan2(y - centroid.y, x - centroid.x);

        let deltaAngle = angle2 - angle1;

        // Normalize to [-PI, PI]
        while (deltaAngle > Math.PI) deltaAngle -= 2 * Math.PI;
        while (deltaAngle < -Math.PI) deltaAngle += 2 * Math.PI;

        // Only accumulate angle if radius is valid
        if (radius >= this.config.rollMinRadiusPx && radius <= maxRadius) {
          state.totalRadians += Math.abs(deltaAngle);

          // Emit progress
          if (this.listener) {
            const progressAction: RollProgressAction = {
              type: "rollProgress",
              radians: state.totalRadians,
            };
            this.listener(progressAction);
          }

          // Check for full lap (2π radians)
          while (state.totalRadians >= 2 * Math.PI) {
            state.totalRadians -= 2 * Math.PI;
            state.completedLaps++;
            this.emitLap(t);
          }
        }
      }
    } else if (phase === "end" || phase === "cancel") {
      this.touches.delete(pointerId);
    }
  }

  private calculateCentroid(samples: Array<{ x: number; y: number }>): {
    x: number;
    y: number;
  } {
    const sum = samples.reduce(
      (acc, s) => ({ x: acc.x + s.x, y: acc.y + s.y }),
      { x: 0, y: 0 },
    );
    return { x: sum.x / samples.length, y: sum.y / samples.length };
  }

  private emitLap(time: number): void {
    // Apply rate cap
    const now = time / 1000;
    this.lastLapTimes = this.lastLapTimes.filter((t) => now - t < 1.0);

    if (this.lastLapTimes.length >= MAX_ROLL_RATE) {
      return; // Drop event due to rate cap
    }

    this.lastLapTimes.push(now);

    if (this.listener) {
      const action: RollLapAction = {
        type: "rollLap",
        tick: this.nowTick(),
      };
      this.listener(action);
    }
  }
}
